/*
  Dárek zdarma nad limit — logika bloku v minicartu.

  Načítá se z theme.liquid (JEDNOU, defer) — sekce mini-cart přichází přes
  Section Rendering API jako innerHTML, takže <script> uvnitř sekce by se
  nikdy nespustil. Custom element se po každém překreslení znovu připojí
  a projede guard logiku jednou (vzor gift-wrapping.js).

  Bezpečnostní zásady (red-team council):
  - všechny mutace košíku přes /cart/update.js s `updates` klíčovanými
    VARIANT ID — nikdy poziční line index (posouvá se, race condition)
  - jeden modulový mutex `busy` — žádné souběžné mutace z tohoto bloku
  - stav `over` (dárek v košíku bez nároku): okamžitě zamknout checkout
    tlačítka, pak dárek odebrat a překreslit — zavírá okno, kdy je dárek
    vidět za plnou cenu s aktivní pokladnou
  - překreslování sekcí BEZ MiniCart.renderContents() (ten násilně otevírá
    drawer); na /cart stránce reload (vzor gift-wrapping.js)
*/

(() => {
  if (customElements.get('gift-threshold')) return;

  let busy = false;

  const STORAGE = {
    choice: 'nebe:gift-choice',      // poslední zvolené hodnoty option → obnova po yo-yo kolem prahu
    dismissed: 'nebe:gift-dismissed',// zákazník dárek ručně smazal → kompaktní re-entry
    removed: 'nebe:gift-removed',    // auto-remove proběhl → jednorázová hláška
    inCart: 'nebe:gift-in-cart',     // dárek byl v minulém renderu v košíku (detekce ručního smazání)
    changing: 'nebe:gift-changing'   // „změnit variantu" → po odebrání ukázat plný výběr
  };

  const SECTIONS = [
    { id: 'mini-cart', section: 'mini-cart', selector: '.shopify-section' },
    { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    { id: 'mobile-cart-icon-bubble', section: 'mobile-cart-icon-bubble', selector: '.shopify-section' }
  ];

  class GiftThreshold extends HTMLElement {
    connectedCallback() {
      this.state = this.dataset.state;
      this.giftLines = this.parseJson(this.dataset.giftLines) || [];
      this.markGiftLines();

      switch (this.state) {
        case 'over':
          this.lockCheckout();
          this.removeGift({ silentNotice: true });
          break;
        case 'added':
          sessionStorage.setItem(STORAGE.inCart, '1');
          sessionStorage.removeItem(STORAGE.removed);
          this.clampQuantity();
          this.bindChange();
          break;
        case 'choose':
          this.detectManualDismiss();
          this.initPicker();
          break;
        default:
          this.showRemovedNotice();
      }
    }

    /* ------------------------------------------------ pomocné */

    parseJson(text) {
      try { return JSON.parse(text); } catch { return null; }
    }

    get variants() {
      if (!this._variants) {
        const el = this.querySelector('[data-gift-variants]');
        this._variants = el ? this.parseJson(el.textContent) || [] : [];
      }
      return this._variants;
    }

    sectionsPayload() {
      return {
        sections: SECTIONS.map((s) => s.section).join(','),
        sections_url: window.location.pathname
      };
    }

    onCartPage() {
      return this.dataset.context === 'page' || !document.getElementById('mini-cart');
    }

    renderSections(parsedState) {
      if (this.onCartPage()) {
        window.location.href = theme.routes.cart_url;
        return;
      }
      SECTIONS.forEach((section) => {
        const container = document.getElementById(section.id);
        const html = parsedState.sections && parsedState.sections[section.id];
        if (!container || !html) return;
        const parsed = new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector);
        if (parsed) container.innerHTML = parsed.innerHTML;
      });
      this.unlockCheckout();
    }

    lockCheckout() {
      document.querySelectorAll('#cart [name="checkout"], form[action$="/cart"] [name="checkout"]')
        .forEach((btn) => { btn.disabled = true; btn.setAttribute('aria-disabled', 'true'); });
    }

    unlockCheckout() {
      document.querySelectorAll('#cart [name="checkout"], form[action$="/cart"] [name="checkout"]')
        .forEach((btn) => { btn.disabled = false; btn.removeAttribute('aria-disabled'); });
    }

    /* Řádky dárku v seznamu položek: skrýt +/− (zdarma je jen 1 ks), nechat koš. */
    markGiftLines() {
      if (!this.giftLines.length) return;
      const ids = this.giftLines.map((l) => String(l.variantId));
      document.querySelectorAll('#main-cart-items li[data-variant-id], .mini-cart li[data-variant-id]')
        .forEach((li) => {
          if (ids.includes(li.dataset.variantId)) li.classList.add('is-gift-line');
        });
    }

    /* ------------------------------------------------ mutace košíku */

    async cartUpdate(updates) {
      const body = JSON.stringify({ updates, ...this.sectionsPayload() });
      const response = await fetch(theme.routes.cart_update_url, { ...fetchConfig(), body });
      return response.json();
    }

    /* Odebrání všech dárkových řádků (ztráta nároku / „změnit variantu"). */
    async removeGift({ silentNotice = false, changing = false } = {}) {
      if (busy || !this.giftLines.length) return;
      busy = true;
      try {
        if (changing) {
          sessionStorage.setItem(STORAGE.changing, '1');
        } else if (silentNotice) {
          sessionStorage.setItem(STORAGE.removed, '1');
        }
        sessionStorage.removeItem(STORAGE.inCart);
        const updates = {};
        this.giftLines.forEach((line) => { updates[line.key] = 0; });
        const state = await this.cartUpdate(updates);
        busy = false;
        this.renderSections(state);
      } catch (error) {
        busy = false;
        this.unlockCheckout();
        console.error('[gift-threshold] remove failed', error);
      }
    }

    /* Zdarma je jen 1 ks — víc kusů / víc variant srazit na jeden řádek à 1 ks. */
    clampQuantity() {
      const total = this.giftLines.reduce((sum, line) => sum + line.quantity, 0);
      if (total <= 1 || busy) return;
      busy = true;
      const updates = {};
      this.giftLines.forEach((line, index) => { updates[line.key] = index === 0 ? 1 : 0; });
      this.cartUpdate(updates)
        .then((state) => { busy = false; this.renderSections(state); })
        .catch((error) => { busy = false; console.error('[gift-threshold] clamp failed', error); });
    }

    async addGift(variantId, button) {
      if (busy) return;
      busy = true;
      // tlačítko už v UI není (auto-add), ale zůstává volitelné pro robustnost/retry;
      // loading stav drží spinner v hintu [data-gift-adding].
      button?.classList.add('loading');
      if (button) button.disabled = true;
      this.querySelector('[data-gift-adding]')?.removeAttribute('hidden');
      try {
        // _gift: skrytá property (prefix _) → značí, že řádek přidal blok.
        // Jen takto označené řádky blok spravuje (stav/odebrání/clamp); běžně
        // koupený dárkový produkt zůstane netknutý.
        const body = JSON.stringify({
          items: [{ id: variantId, quantity: 1, properties: { _gift: 'true' } }],
          ...this.sectionsPayload()
        });
        const response = await fetch(theme.routes.cart_add_url, { ...fetchConfig(), body });
        const data = await response.json();
        busy = false;
        if (data.status) {
          // 422 — varianta mezitím došla apod.
          button?.classList.remove('loading');
          this.querySelector('[data-gift-adding]')?.setAttribute('hidden', '');
          this.showError(data.description || data.message);
          this.refreshAvailability();
          return;
        }
        sessionStorage.removeItem(STORAGE.dismissed);
        sessionStorage.removeItem(STORAGE.changing);
        sessionStorage.setItem(STORAGE.inCart, '1');
        this.renderSections(data);
        document.dispatchEvent(new CustomEvent('gift-threshold:added', { detail: { variantId } }));
      } catch (error) {
        busy = false;
        button?.classList.remove('loading');
        if (button) button.disabled = false;
        this.querySelector('[data-gift-adding]')?.setAttribute('hidden', '');
        this.showError();
        console.error('[gift-threshold] add failed', error);
      }
    }

    /* ------------------------------------------------ stav choose */

    detectManualDismiss() {
      // Dárek byl v košíku (minulý render), nárok trvá, teď v košíku není
      // a nejde o „změnit variantu" → zákazník ho smazal sám. Respektovat.
      if (sessionStorage.getItem(STORAGE.changing)) {
        sessionStorage.removeItem(STORAGE.changing);
        sessionStorage.removeItem(STORAGE.inCart);
        return;
      }
      if (sessionStorage.getItem(STORAGE.inCart)) {
        sessionStorage.removeItem(STORAGE.inCart);
        sessionStorage.setItem(STORAGE.dismissed, '1');
      }
      if (sessionStorage.getItem(STORAGE.dismissed)) {
        this.querySelector('[data-gift-full]')?.classList.add('hidden');
        const reentry = this.querySelector('[data-gift-expand]');
        reentry?.classList.remove('hidden');
        reentry?.addEventListener('click', () => {
          sessionStorage.removeItem(STORAGE.dismissed);
          reentry.classList.add('hidden');
          this.querySelector('[data-gift-full]')?.classList.remove('hidden');
        });
      }
    }

    initPicker() {
      this.selection = {};
      this.optionCount = this.querySelectorAll('fieldset[data-option-index]').length;
      this.addButton = this.querySelector('[data-gift-add]');
      this.hint = this.querySelector('[data-gift-hint]');

      this.querySelectorAll('input[data-option-value]').forEach((input) => {
        input.addEventListener('change', () => {
          const index = Number(input.closest('fieldset').dataset.optionIndex);
          this.selection[index] = input.value;
          this.refreshAvailability();
          // AUTO-ADD: jakmile je vybraná barva i velikost (a varianta je dostupná),
          // přidej dárek rovnou — bez nutnosti klikat na „Přidat dárek zdarma".
          // (Zákaznice jinak proklikly k pokladně bez dárku.) Spouští se jen z user
          // gesta (obnova uložené volby nevyvolá 'change'); busy hlídá dvojí přidání.
          const variant = this.matchedVariant();
          if (variant && variant.available) {
            sessionStorage.setItem(STORAGE.choice, JSON.stringify(this.selection));
            this.addGift(variant.id, this.addButton);
          }
        });
      });

      // Obnova dřívější volby (yo-yo kolem prahu, „změnit variantu").
      const saved = this.parseJson(sessionStorage.getItem(STORAGE.choice));
      if (saved) {
        Object.entries(saved).forEach(([index, value]) => {
          const input = this.querySelector(
            `fieldset[data-option-index="${index}"] input[data-option-value][value="${CSS.escape(value)}"]`
          );
          if (input && !input.disabled) {
            input.checked = true;
            this.selection[index] = value;
          }
        });
      }
      this.refreshAvailability();
      this.bindGallery();

      this.addButton?.addEventListener('click', () => {
        const variant = this.matchedVariant();
        if (!variant || !variant.available) return;
        sessionStorage.setItem(STORAGE.choice, JSON.stringify(this.selection));
        this.addGift(variant.id, this.addButton);
      });
    }

    /* ------------------------------------------------ galerie dárku (lightbox) */

    get media() {
      if (!this._media) {
        const el = this.querySelector('[data-gift-media]');
        this._media = el ? this.parseJson(el.textContent) || [] : [];
      }
      return this._media;
    }

    bindGallery() {
      const trigger = this.querySelector('[data-gift-gallery]');
      if (!trigger || !this.media.length) return;
      trigger.addEventListener('click', () => this.openLightbox(0));
    }

    // Lightbox se připojuje na <body> (mimo drawer, ať se neořízne), z-index nad vším.
    openLightbox(startIndex) {
      const media = this.media;
      if (!media.length || document.querySelector('.gift-lightbox')) return;
      const esc = (s) => String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
      const multi = media.length > 1;
      const box = document.createElement('div');
      box.className = 'gift-lightbox';
      box.setAttribute('role', 'dialog');
      box.setAttribute('aria-modal', 'true');
      box.setAttribute('aria-label', this.querySelector('.gift-threshold__product')?.textContent || 'Galerie');
      const slides = media
        .map((m, i) => `<div class="gift-lightbox__slide"><img src="${esc(m.url)}" alt="${esc(m.alt)}" loading="${i === 0 ? 'eager' : 'lazy'}"></div>`)
        .join('');
      box.innerHTML =
        `<div class="gift-lightbox__track" data-lb-track>${slides}</div>` +
        `<button type="button" class="gift-lightbox__close" data-lb-close aria-label="Zavřít">×</button>` +
        (multi
          ? `<button type="button" class="gift-lightbox__nav gift-lightbox__nav--prev" data-lb-prev aria-label="Předchozí">‹</button>` +
            `<button type="button" class="gift-lightbox__nav gift-lightbox__nav--next" data-lb-next aria-label="Další">›</button>` +
            `<div class="gift-lightbox__count" data-lb-count>1/${media.length}</div>`
          : '');
      document.body.appendChild(box);
      const prevOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';

      const track = box.querySelector('[data-lb-track]');
      const count = box.querySelector('[data-lb-count]');
      const current = () => {
        let best = 0, bd = Infinity;
        for (let i = 0; i < track.children.length; i++) {
          const d = Math.abs(track.children[i].offsetLeft - track.scrollLeft);
          if (d < bd) { bd = d; best = i; }
        }
        return best;
      };
      const goTo = (i) => {
        i = Math.max(0, Math.min(media.length - 1, i));
        const el = track.children[i];
        if (el) track.scrollTo ? track.scrollTo({ left: el.offsetLeft, behavior: 'smooth' }) : (track.scrollLeft = el.offsetLeft);
      };
      if (multi) {
        track.addEventListener('scroll', () => { count.textContent = current() + 1 + '/' + media.length; }, { passive: true });
        box.querySelector('[data-lb-prev]').addEventListener('click', () => goTo(current() - 1));
        box.querySelector('[data-lb-next]').addEventListener('click', () => goTo(current() + 1));
      }
      // Minicart drawer (Be Yours details-modal) zavírá na keyup Escape i na body-click mimo
      // drawer → lightbox musí obě tyhle události spolknout, ať se pod ním drawer nezavře.
      // POZOR: Escape zavíráme na keyUP (ne keydown), jinak by keydown zavřel lightbox +
      // odebral listenery dřív, než dorazí keyup Escape → ten by pak zavřel i drawer.
      const close = () => {
        box.classList.add('is-closing');
        document.body.style.overflow = prevOverflow;
        window.removeEventListener('keydown', onKeyDown, true);
        window.removeEventListener('keyup', onKeyUp, true);
        setTimeout(() => box.remove(), 180);
      };
      const onKeyDown = (e) => {
        if (e.key === 'Escape') e.stopPropagation();                 // spolkni; zavírá až keyup
        else if (multi && e.key === 'ArrowLeft') { e.stopPropagation(); goTo(current() - 1); }
        else if (multi && e.key === 'ArrowRight') { e.stopPropagation(); goTo(current() + 1); }
      };
      const onKeyUp = (e) => {
        if (e.key === 'Escape') { e.stopPropagation(); close(); }
      };
      // capture na window → doběhne dřív než listenery draweru a stopPropagation je zabije
      window.addEventListener('keydown', onKeyDown, true);
      window.addEventListener('keyup', onKeyUp, true);
      box.querySelector('[data-lb-close]').addEventListener('click', close);
      // KLÍČOVÉ: klik uvnitř lightboxu nesmí probublat k drawer body-click handleru
      // (jinak = klik „mimo drawer" → minicart se zavře). Backdrop/okraj slidu zavře jen lightbox.
      box.addEventListener('click', (e) => {
        e.stopPropagation();
        if (e.target === box || e.target.classList.contains('gift-lightbox__slide')) close();
      });
      if (startIndex) requestAnimationFrame(() => goTo(startIndex));
    }

    matchedVariant() {
      if (Object.keys(this.selection).length < this.optionCount) return null;
      return this.variants.find((variant) =>
        variant.options.every((value, index) => this.selection[index] === value)
      );
    }

    /* Hodnota je nedostupná, když s ní (při respektování OSTATNÍCH zvolených
       hodnot) neexistuje žádná dostupná varianta. */
    refreshAvailability() {
      this.querySelectorAll('fieldset[data-option-index]').forEach((fieldset) => {
        const index = Number(fieldset.dataset.optionIndex);
        fieldset.querySelectorAll('input[data-option-value]').forEach((input) => {
          const possible = this.variants.some((variant) => {
            if (!variant.available || variant.options[index] !== input.value) return false;
            return variant.options.every((value, i) => {
              if (i === index) return true;
              return this.selection[i] === undefined || this.selection[i] === value;
            });
          });
          input.disabled = !possible;
          input.closest('label').classList.toggle('is-unavailable', !possible);
          if (!possible && input.checked) {
            input.checked = false;
            delete this.selection[index];
          }
        });
      });

      const variant = this.matchedVariant();
      const complete = Object.keys(this.selection).length === this.optionCount;
      if (this.addButton) this.addButton.disabled = !(complete && variant && variant.available);
      if (this.hint) this.hint.classList.toggle('hidden', complete);
    }

    showError(message) {
      const error = this.querySelector('[data-gift-error]');
      if (!error) return;
      if (message) error.querySelector('span').textContent = message;
      error.classList.remove('hidden');
    }

    /* ------------------------------------------------ stav added / idle */

    bindChange() {
      this.querySelector('[data-gift-change]')?.addEventListener('click', () => {
        this.removeGift({ changing: true });
      });
    }

    showRemovedNotice() {
      const slot = this.querySelector('[data-gift-notice-slot]');
      if (!slot || !sessionStorage.getItem(STORAGE.removed)) return;
      sessionStorage.removeItem(STORAGE.removed);
      const notice = document.createElement('p');
      notice.className = 'gift-threshold__notice caption-large';
      notice.setAttribute('role', 'status');
      notice.textContent = slot.dataset.removedText;
      slot.appendChild(notice);
    }
  }

  customElements.define('gift-threshold', GiftThreshold);
})();
